import React, { useState, useEffect } from "react";
import { Modal, Autocomplete, TextField, Box, Grid, Checkbox } from "@mui/material";
import { Form, Formik } from "formik";
import { enqueueSnackbar } from "notistack";
import EditIcon from "@mui/icons-material/Edit";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

import * as Yup from "yup";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const createModalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "80%",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  "& .MuiFormHelperText-root": { color: "#d32f2f" },
  borderRadius: "0.5rem",
  outline: "none",
};

function CustomizeLeaderboardModal({
  open,
  handleClose,
  selectionListOptions,
  handleSave,
  widgetOptions,
}) {
  const { kpiId, kpiName, title, timePeriod, teams } = widgetOptions;
  const [titleEditable, setTitleEditable] = useState(false);
  const [initialValues, setInitialValues] = useState({
    kpi: { id: kpiId, name: kpiName },
    title,
    timePeriod: timePeriod.toLowerCase(),
    teams: teams && teams.length ? teams : [0],
  });
  const [submitLoading, setSubmitLoading] = useState(false);
  const [teamOptions, setTeamOptions] = useState([]);

  const timePeriods = ["Daily", "Weekly", "Monthly", "Quarterly", "Yearly"];

  const validationSchema = Yup.object({
    title: Yup.string()
      .max(30, "Title cannot be more than 30 characters")
      .required("Title is required"),
  });

  const handleTitleEditClick = () => {
    setTitleEditable(true);
  };

  const handleModalClose = () => {
    if (!submitLoading) {
      setTitleEditable(false);
      handleClose();
    }
  };

  const handleSubmit = async (data) => {
    try {
      setSubmitLoading(true);

      const obj = {
        kpiId: data.kpi?.id,
        kpiName: data.kpi?.name,
        title: data.title,
        timePeriod: data.timePeriod,
        teams: data.teams,
      };

      handleSave(obj);
      setInitialValues(data);

      setSubmitLoading(false);
      handleModalClose();
    } catch (error) {
      setSubmitLoading(false);
      handleModalClose();
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error.message || "Something Went Wrong",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  useEffect(() => {
    const options = selectionListOptions.teamOptions.map((option) => ({
      group: "TEAMS",
      ...option,
    }));

    options.unshift({
      id: 0,
      name: "All teams in company",
      group: "ALL",
    });

    setTeamOptions(options);
  }, [open]);

  return (
    <Modal
      open={open}
      onClose={handleModalClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <MDBox
        sx={{
          ...createModalStyle,
          width: {
            xs: "90%",
            md: "600px",
          },
        }}
      >
        <Formik
          enableReinitialize
          validateOnChange
          validationSchema={validationSchema}
          initialValues={initialValues}
          onSubmit={handleSubmit}
        >
          {({ values, setFieldValue, touched, errors, dirty }) => (
            <Form>
              <Box sx={{ display: "inline-flex" }}>
                {titleEditable ? (
                  <TextField
                    label="Widget Title"
                    defaultValue={values.title || "Leaderboard"}
                    onChange={(event) => setFieldValue("title", event.target.value)}
                    error={Boolean(touched.title && errors.title)}
                    helperText={touched.title && errors.title}
                    sx={{ mb: "1rem" }}
                    inputProps={{ maxLength: 30 }}
                  />
                ) : (
                  <MDTypography
                    sx={{ mb: "1rem", mr: "0.25rem" }}
                    id="modal-modal-title"
                    variant="h5"
                    component="h2"
                  >
                    {title || "Leaderboard"}
                  </MDTypography>
                )}
                {!titleEditable && (
                  <EditIcon
                    sx={{ cursor: "pointer", position: "relative", top: "3px" }}
                    onClick={() => {
                      handleTitleEditClick();
                    }}
                  />
                )}
              </Box>
              <Grid container sx={{ display: "flex", mb: "1rem" }}>
                <Grid item xs={6} sx={{ pr: "0.5rem" }}>
                  <Autocomplete
                    multiple
                    options={teamOptions}
                    disableCloseOnSelect
                    // limitTags={3}
                    groupBy={(option) => option.group}
                    getOptionLabel={(option) => option.name}
                    onChange={(_, value) =>
                      setFieldValue(
                        "teams",
                        value.map((t) => t.id)
                      )
                    }
                    value={teamOptions.filter((option) => values.teams.includes(option.id))}
                    sx={{
                      "span.MuiAutocomplete-tag": {
                        backgroundColor: "#fff",
                        color: "#000",
                      },
                      ".MuiChip-root": {
                        backgroundColor: "#333",
                      },
                    }}
                    renderOption={(props, option, { selected }) => (
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      <li {...props}>
                        <Checkbox
                          icon={icon}
                          checkedIcon={checkedIcon}
                          style={{ marginRight: 8 }}
                          checked={selected}
                        />
                        {option.name}
                      </li>
                    )}
                    renderInput={(params) => <TextField {...params} label="Filter Teams" />}
                  />
                </Grid>
                <Grid item xs={6} sx={{ pl: "0.5rem" }}>
                  <Autocomplete
                    disablePortal
                    disableClearable
                    value={
                      (values.timePeriod &&
                        timePeriods.find((option) => option.toLowerCase() === values.timePeriod)) ||
                      null
                    }
                    onChange={(_, value) => setFieldValue("timePeriod", value.toLowerCase())}
                    options={timePeriods}
                    renderInput={(params) => <TextField {...params} label="Display Period" />}
                  />
                </Grid>
              </Grid>
              <Box>
                <Autocomplete
                  disablePortal
                  disableClearable
                  options={selectionListOptions.kpiOptions}
                  getOptionLabel={(option) => option.name}
                  value={
                    (values.kpi.id &&
                      selectionListOptions.kpiOptions.find(
                        (option) => option.id === values.kpi.id
                      )) ||
                    null
                  }
                  sx={{ width: "100%" }}
                  onChange={(_, value) => setFieldValue("kpi", value)}
                  renderInput={(params) => <TextField {...params} label="KPI" />}
                />
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", float: "right" }}>
                <Box>
                  <MDButton
                    color="gray80"
                    onClick={handleModalClose}
                    sx={{ mt: 2, mr: 1, position: "relative" }}
                  >
                    Cancel
                  </MDButton>
                  <MDButton
                    color="primaryRed"
                    type="submit"
                    disabled={submitLoading || !dirty}
                    sx={{ mt: 2, position: "relative" }}
                  >
                    {submitLoading ? "Loading..." : "Save"}
                  </MDButton>
                </Box>
              </Box>
            </Form>
          )}
        </Formik>
      </MDBox>
    </Modal>
  );
}

export default CustomizeLeaderboardModal;
